import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { AuthProvider } from "@/hooks/useAuth";
import Index from "./pages/Index.tsx";
import DataLayerPageView from "./components/DataLayerPageView.tsx";
import TopNav from "./components/TopNav.tsx";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Loader2 } from "lucide-react";

const PageFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <Loader2 className="w-6 h-6 animate-spin text-primary" />
  </div>
);

// Lazy-loaded routes for faster initial bundle
const Auth = lazy(() => import("./pages/Auth.tsx"));
const Checkout = lazy(() => import("./pages/Checkout.tsx"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions.tsx"));
const FAQ = lazy(() => import("./pages/FAQ.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const MarketNews = lazy(() => import("./pages/MarketNews.tsx"));
const RunHistory = lazy(() => import("./pages/RunHistory.tsx"));
const AlertSettings = lazy(() => import("./pages/AlertSettings.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const Profile = lazy(() => import("./pages/Profile.tsx"));
const SeoAdmin = lazy(() => import("./pages/SeoAdmin.tsx"));
const SeoPage = lazy(() => import("./pages/SeoPage.tsx"));
const SeoPreview = lazy(() => import("./pages/SeoPreview.tsx"));
const TrackRecord = lazy(() => import("./pages/TrackRecord.tsx"));
const Watchlist = lazy(() => import("./pages/Watchlist.tsx"));
const ScenarioSimulator = lazy(() => import("./pages/ScenarioSimulator.tsx"));
const StockPage = lazy(() => import("./pages/ProgrammaticPages.tsx").then(m => ({ default: m.StockPage })));
const ComparePage = lazy(() => import("./pages/ProgrammaticPages.tsx").then(m => ({ default: m.ComparePage })));
const QuestionPage = lazy(() => import("./pages/ProgrammaticPages.tsx").then(m => ({ default: m.QuestionPage })));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const Compare = lazy(() => import("./pages/Compare.tsx"));
const PickOfTheDay = lazy(() => import("./pages/PickOfTheDay.tsx"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <DataLayerPageView />
            <TopNav />
            <main id="main-content">
            <Suspense fallback={<PageFallback />}>
              <ErrorBoundary>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/history" element={<RunHistory />} />
                <Route path="/alerts" element={<AlertSettings />} />
                <Route path="/profile" element={<Profile />} />

                <Route path="/checkout" element={<Checkout />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/terms" element={<TermsAndConditions />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/news" element={<MarketNews />} />
                <Route path="/track-record" element={<TrackRecord />} />
                <Route path="/pick-of-the-day" element={<PickOfTheDay />} />
                <Route path="/watchlist" element={<Watchlist />} />
                <Route path="/scenario" element={<ScenarioSimulator />} />
                <Route path="/stocks/:ticker" element={<StockPage />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/compare/:pair" element={<ComparePage />} />
                <Route path="/q/:slug" element={<QuestionPage />} />
                <Route path="/blog" element={<Blog />} />

                {/* SEO Pages */}
                <Route path="/seo-admin" element={<SeoAdmin />} />
                <Route path="/seo-preview/:id" element={<SeoPreview />} />
                <Route path="/p/:slug" element={<SeoPage />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
              </ErrorBoundary>
            </Suspense>
            </main>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
